/**
 * ParticleRegistry.ts
 *
 * Global registry to track all active particles in the game.
 * Each particle system registers/unregisters particles here for debugging.
 */

export interface ParticleStats {
  /** Category name -> count */
  [category: string]: number;
}

/**
 * Global singleton to track particle counts across all systems
 */
class ParticleRegistryClass {
  private counts: Map<string, number> = new Map();

  /**
   * Register particles being created
   * @param category Category name (e.g., "burnMarks", "risingLines", "coins")
   * @param count Number of particles created (default: 1)
   */
  add(category: string, count: number = 1): void {
    const current = this.counts.get(category) || 0;
    this.counts.set(category, current + count);
  }

  /**
   * Unregister particles being destroyed
   * @param category Category name
   * @param count Number of particles destroyed (default: 1)
   */
  remove(category: string, count: number = 1): void {
    const current = this.counts.get(category) || 0;
    this.counts.set(category, Math.max(0, current - count));
  }

  /**
   * Set the exact count for a category (useful for systems that track their own count)
   * @param category Category name
   * @param count Exact count
   */
  set(category: string, count: number): void {
    this.counts.set(category, count);
  }

  /**
   * Get count for a specific category
   */
  get(category: string): number {
    return this.counts.get(category) || 0;
  }

  /**
   * Get total count across all categories
   */
  getTotal(): number {
    let total = 0;
    for (const count of this.counts.values()) {
      total += count;
    }
    return total;
  }

  /**
   * Get all stats as an object
   */
  getStats(): ParticleStats {
    const stats: ParticleStats = {};
    for (const [category, count] of this.counts) {
      stats[category] = count;
    }
    return stats;
  }

  /**
   * Get formatted string for debug display
   */
  getDebugString(): string {
    const lines: string[] = [];
    let total = 0;

    // Sort categories alphabetically
    const sortedCategories = Array.from(this.counts.keys()).sort();

    for (const category of sortedCategories) {
      const count = this.counts.get(category) || 0;
      if (count > 0) {
        lines.push(`  ${category}: ${count}`);
        total += count;
      }
    }

    if (lines.length === 0) {
      return `Total particles: 0`;
    }

    return `Total particles: ${total}\n${lines.join('\n')}`;
  }

  /**
   * Clear all counts (useful for scene transitions)
   */
  clear(): void {
    this.counts.clear();
  }
}

// Global singleton instance
export const ParticleRegistry = new ParticleRegistryClass();
